/**
 * Org Structure API — divisions, departments, roles, global roles, role
 * publications, role assignments, composed org-chart endpoint.
 *
 * Mirrors apps/chaos-gateway/src/org-structure.ts (Milestone 1.5a).
 *
 * Wire shape from @chaos/types/org-structure.
 */
import type {
  CreateDepartmentInput,
  CreateDivisionInput,
  CreateRoleAssignmentInput,
  CreateRoleInput,
  CreateRolePublicationInput,
  Department,
  Division,
  OrgChart,
  ReorderDepartmentInput,
  ReorderDivisionInput,
  Role,
  RoleAssignment,
  RoleListItem,
  RolePublication,
  UpdateDepartmentInput,
  UpdateDivisionInput,
  UpdateRoleInput,
  UpdateRolePublicationInput,
} from '@chaos/types/org-structure';

import { apiJson } from './client';

// ── Response wrappers ────────────────────────────────────────────────
interface DivisionsListResponse {
  divisions: Division[];
}
interface DivisionResponse {
  division: Division;
}
interface DepartmentsListResponse {
  departments: Department[];
}
interface DepartmentResponse {
  department: Department;
}
// ── THE LIST SHAPE, NOT THE ROLE ──────────────────────────────────────
// `GET /api/orgs/:orgId/roles` stopped emitting `systemPrompt`,
// `instructions`, `permissionMode`, `allowedTools`, `fullVpsAccess`,
// `defaultAutonomy`, `viewScope` and `canAutoApprove` on 2026-08-12 — a picker
// is not a place to read a role's prompt, and any member of a Workspace could.
// `RoleListItem` is that wire shape, so the type stops promising fields the
// server no longer sends.
//
// `GET /global-roles` is founder-only and still answers with FULL roles; it
// keeps `Role[]` below. Every `Role` is assignable to `RoleListItem`, so the
// two lists still meet in the same table components.
interface RolesListResponse {
  roles: RoleListItem[];
}
interface GlobalRolesListResponse {
  roles: Role[];
}
interface RoleResponse {
  role: Role;
}
interface PublicationsListResponse {
  publications: RolePublication[];
}
interface PublicationResponse {
  publication: RolePublication;
}
interface AssignmentsListResponse {
  assignments: RoleAssignment[];
}
interface AssignmentResponse {
  assignment: RoleAssignment;
}
interface OrgChartResponse {
  orgChart: OrgChart;
}

/**
 * WHAT ARCHIVING THIS ROW WILL DO — read BEFORE the founder agrees.
 *
 * Archiving a division, department or role relocates its children in the same
 * transaction (departments to "no division", roles to "no department", child
 * departments and subordinate roles to the grandparent) and revokes the row
 * from every running agent that materialised it. None of that was visible: the
 * only warning was `window.confirm('Archive this entry? This is soft delete.')`.
 * This is the column-delete dialog's shape, one level up the tree.
 */
export interface ArchiveImpact {
  kind: 'division' | 'department' | 'role' | 'globalRole';
  id: string;
  name: string;
  children: {
    departments?: number;
    roles?: number;
    childDepartments?: number;
    subordinateRoles?: number;
  };
  /** Roles only: live cards whose role_id or assignee_role_id names this role. */
  cardsReferencing?: number;
  /** Where the children land, in words. null = there are none to move. */
  movedTo: string | null;
  /** Interactive sessions that will rebuild their container as a result. */
  sessionsToRebuild: number;
  /** false when the gateway TRASH_ENABLED flag is off — do not promise a restore. */
  restorable: boolean;
}

/**
 * One sentence for the confirm dialog. Pure, and deliberately separate from the
 * component: it is the part that must not silently start saying "0 roles will
 * move" when the field is renamed, so it is the part that gets unit-tested.
 */
export function describeArchiveImpact(i: ArchiveImpact): string {
  const moves: string[] = [];
  if (i.children.departments) {
    moves.push(`${i.children.departments} department${i.children.departments === 1 ? '' : 's'}`);
  }
  if (i.children.roles) {
    moves.push(`${i.children.roles} role${i.children.roles === 1 ? '' : 's'}`);
  }
  if (i.children.childDepartments) {
    moves.push(
      `${i.children.childDepartments} sub-department${i.children.childDepartments === 1 ? '' : 's'}`,
    );
  }
  if (i.children.subordinateRoles) {
    const n = i.children.subordinateRoles;
    moves.push(`${n} role${n === 1 ? ' that reports' : 's that report'} to it`);
  }
  const parts: string[] = [];
  parts.push(
    moves.length > 0 && i.movedTo
      ? `${moves.join(' and ')} will move to ${i.movedTo}, so it comes back empty if you restore it.`
      : 'It has nothing under it, so nothing moves.',
  );
  if (i.cardsReferencing) {
    const n = i.cardsReferencing;
    parts.push(
      `${n} card${n === 1 ? ' still names' : 's still name'} it; they keep working and keep the reference.`,
    );
  }
  if (i.sessionsToRebuild > 0) {
    const n = i.sessionsToRebuild;
    parts.push(
      `${n} running session${n === 1 ? ' loses' : 's lose'} it when the current turn ends, and rebuild.`,
    );
  }
  parts.push(
    i.restorable
      ? 'It moves to Trash and can be restored.'
      : 'Trash is turned off on this instance, so this cannot be undone from the UI.',
  );
  return parts.join(' ');
}

export const orgStructureApi = {
  // ── Divisions ─────────────────────────────────────────────────────
  listDivisions(orgId: string): Promise<Division[]> {
    return apiJson<DivisionsListResponse>(`/orgs/${orgId}/divisions`).then(
      (r) => r.divisions,
    );
  },
  createDivision(orgId: string, input: CreateDivisionInput): Promise<Division> {
    return apiJson<DivisionResponse>(`/orgs/${orgId}/divisions`, {
      method: 'POST',
      json: input,
    }).then((r) => r.division);
  },
  updateDivision(
    orgId: string,
    divisionId: string,
    input: UpdateDivisionInput,
  ): Promise<Division> {
    return apiJson<DivisionResponse>(`/orgs/${orgId}/divisions/${divisionId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.division);
  },
  archiveDivision(orgId: string, divisionId: string): Promise<Division> {
    return apiJson<DivisionResponse>(`/orgs/${orgId}/divisions/${divisionId}`, {
      method: 'DELETE',
    }).then((r) => r.division);
  },
  reorderDivisions(orgId: string, input: ReorderDivisionInput): Promise<Division[]> {
    return apiJson<DivisionsListResponse>(`/orgs/${orgId}/divisions/reorder`, {
      method: 'POST',
      json: input,
    }).then((r) => r.divisions);
  },

  // ── Departments ───────────────────────────────────────────────────
  listDepartments(orgId: string, divisionId?: string): Promise<Department[]> {
    const qs = divisionId ? `?divisionId=${encodeURIComponent(divisionId)}` : '';
    return apiJson<DepartmentsListResponse>(`/orgs/${orgId}/departments${qs}`).then(
      (r) => r.departments,
    );
  },
  getDepartment(orgId: string, departmentId: string): Promise<Department> {
    return apiJson<DepartmentResponse>(`/orgs/${orgId}/departments/${departmentId}`).then(
      (r) => r.department,
    );
  },
  createDepartment(orgId: string, input: CreateDepartmentInput): Promise<Department> {
    return apiJson<DepartmentResponse>(`/orgs/${orgId}/departments`, {
      method: 'POST',
      json: input,
    }).then((r) => r.department);
  },
  updateDepartment(
    orgId: string,
    departmentId: string,
    input: UpdateDepartmentInput,
  ): Promise<Department> {
    return apiJson<DepartmentResponse>(`/orgs/${orgId}/departments/${departmentId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.department);
  },
  archiveDepartment(
    orgId: string,
    departmentId: string,
  ): Promise<{ department: { id: string; archivedAt: string } }> {
    return apiJson<{ department: { id: string; archivedAt: string } }>(
      `/orgs/${orgId}/departments/${departmentId}`,
      { method: 'DELETE' },
    );
  },
  reorderDepartments(
    orgId: string,
    input: ReorderDepartmentInput,
  ): Promise<Department[]> {
    return apiJson<DepartmentsListResponse>(`/orgs/${orgId}/departments/reorder`, {
      method: 'POST',
      json: input,
    }).then((r) => r.departments);
  },

  // ── Roles ─────────────────────────────────────────────────────────
  // Default ('available') returns org-scoped roles UNIONed with globals
  // published to the org (incl. the all-orgs sentinel) — what pickers want.
  // Pass scope 'own' for ONLY org-scoped roles (the /company/roles editor).
  listRoles(
    orgId: string,
    scope: 'available' | 'own' = 'available',
  ): Promise<RoleListItem[]> {
    const qs = scope === 'own' ? '?scope=own' : '';
    return apiJson<RolesListResponse>(`/orgs/${orgId}/roles${qs}`).then((r) => r.roles);
  },
  getRole(orgId: string, roleId: string): Promise<Role> {
    return apiJson<RoleResponse>(`/orgs/${orgId}/roles/${roleId}`).then((r) => r.role);
  },
  createRole(orgId: string, input: CreateRoleInput): Promise<Role> {
    return apiJson<RoleResponse>(`/orgs/${orgId}/roles`, {
      method: 'POST',
      json: input,
    }).then((r) => r.role);
  },
  updateRole(orgId: string, roleId: string, input: UpdateRoleInput): Promise<Role> {
    return apiJson<RoleResponse>(`/orgs/${orgId}/roles/${roleId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.role);
  },
  archiveRole(
    orgId: string,
    roleId: string,
  ): Promise<{ role: { id: string; archivedAt: string } }> {
    return apiJson<{ role: { id: string; archivedAt: string } }>(
      `/orgs/${orgId}/roles/${roleId}`,
      { method: 'DELETE' },
    );
  },

  // ── Global Roles (system founder only on writes) ──────────────────
  listGlobalRoles(): Promise<Role[]> {
    return apiJson<GlobalRolesListResponse>('/global-roles').then((r) => r.roles);
  },
  getGlobalRole(roleId: string): Promise<Role> {
    return apiJson<RoleResponse>(`/global-roles/${roleId}`).then((r) => r.role);
  },
  createGlobalRole(input: CreateRoleInput): Promise<Role> {
    return apiJson<RoleResponse>('/global-roles', {
      method: 'POST',
      json: input,
    }).then((r) => r.role);
  },
  updateGlobalRole(roleId: string, input: UpdateRoleInput): Promise<Role> {
    return apiJson<RoleResponse>(`/global-roles/${roleId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.role);
  },
  archiveGlobalRole(roleId: string): Promise<{ role: { id: string; archivedAt: string } }> {
    return apiJson<{ role: { id: string; archivedAt: string } }>(`/global-roles/${roleId}`, {
      method: 'DELETE',
    });
  },

  // ── Role Publications ─────────────────────────────────────────────
  listPublishedRoles(orgId: string): Promise<RolePublication[]> {
    return apiJson<PublicationsListResponse>(`/orgs/${orgId}/published-roles`).then(
      (r) => r.publications,
    );
  },
  publishRole(orgId: string, input: CreateRolePublicationInput): Promise<RolePublication> {
    return apiJson<PublicationResponse>(`/orgs/${orgId}/published-roles`, {
      method: 'POST',
      json: input,
    }).then((r) => r.publication);
  },
  updatePublishedRole(
    orgId: string,
    globalRoleId: string,
    input: UpdateRolePublicationInput,
  ): Promise<RolePublication> {
    return apiJson<PublicationResponse>(`/orgs/${orgId}/published-roles/${globalRoleId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.publication);
  },
  unpublishRole(orgId: string, globalRoleId: string): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>(`/orgs/${orgId}/published-roles/${globalRoleId}`, {
      method: 'DELETE',
    });
  },

  // ── Role Assignments ──────────────────────────────────────────────
  listRoleAssignments(orgId: string): Promise<RoleAssignment[]> {
    return apiJson<AssignmentsListResponse>(`/orgs/${orgId}/role-assignments`).then(
      (r) => r.assignments,
    );
  },
  createRoleAssignment(
    orgId: string,
    input: CreateRoleAssignmentInput,
  ): Promise<RoleAssignment> {
    return apiJson<AssignmentResponse>(`/orgs/${orgId}/role-assignments`, {
      method: 'POST',
      json: input,
    }).then((r) => r.assignment);
  },
  endRoleAssignment(orgId: string, assignmentId: string): Promise<RoleAssignment> {
    return apiJson<AssignmentResponse>(
      `/orgs/${orgId}/role-assignments/${assignmentId}`,
      { method: 'DELETE' },
    ).then((r) => r.assignment);
  },

  // ── Archive impact (read-only preview for the confirm dialog) ─────
  archiveImpactDivision(orgId: string, id: string): Promise<ArchiveImpact> {
    return apiJson<ArchiveImpact>(`/orgs/${orgId}/divisions/${id}/archive-impact`);
  },
  archiveImpactDepartment(orgId: string, id: string): Promise<ArchiveImpact> {
    return apiJson<ArchiveImpact>(`/orgs/${orgId}/departments/${id}/archive-impact`);
  },
  archiveImpactRole(orgId: string, id: string): Promise<ArchiveImpact> {
    return apiJson<ArchiveImpact>(`/orgs/${orgId}/roles/${id}/archive-impact`);
  },
  archiveImpactGlobalRole(id: string): Promise<ArchiveImpact> {
    return apiJson<ArchiveImpact>(`/global-roles/${id}/archive-impact`);
  },

  // ── Composed org-chart ────────────────────────────────────────────
  getOrgChart(orgId: string): Promise<OrgChart> {
    return apiJson<OrgChartResponse>(`/orgs/${orgId}/org-chart`).then((r) => r.orgChart);
  },
};

// ── Query-key factory ───────────────────────────────────────────────
export const orgStructureKeys = {
  all: ['org-structure'] as const,

  divisions: (orgId: string) => [...orgStructureKeys.all, 'divisions', orgId] as const,
  departments: (orgId: string) => [...orgStructureKeys.all, 'departments', orgId] as const,
  department: (orgId: string, departmentId: string) =>
    [...orgStructureKeys.all, 'department', orgId, departmentId] as const,

  roles: (orgId: string) => [...orgStructureKeys.all, 'roles', orgId] as const,
  role: (orgId: string, roleId: string) =>
    [...orgStructureKeys.all, 'role', orgId, roleId] as const,

  globalRoles: () => [...orgStructureKeys.all, 'global-roles'] as const,
  globalRole: (roleId: string) => [...orgStructureKeys.all, 'global-role', roleId] as const,

  publishedRoles: (orgId: string) =>
    [...orgStructureKeys.all, 'published-roles', orgId] as const,
  roleAssignments: (orgId: string) =>
    [...orgStructureKeys.all, 'role-assignments', orgId] as const,

  orgChart: (orgId: string) => [...orgStructureKeys.all, 'org-chart', orgId] as const,

  archiveImpact: (kind: string, orgId: string | null, id: string) =>
    [...orgStructureKeys.all, 'archive-impact', kind, orgId ?? 'global', id] as const,
};
